/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, March 30th 2022, 3:16:03 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { FormConfiguration } from "lib/schemas/service-builder/form-builder/Form";
import { FormField } from "lib/schemas/service-builder/form-builder/Field";
import { schemaBuilder, Many2OneUUIDPKBaseSchema } from "lib/validation";

export enum CONDITION_OPERATORS {
  is_equal = "Is Equal To",
  is_greater_than_or_equal_to = "Is Greater than or Equal To",
  is_greater_than = "Is Greater Than",
  is_less_than_or_equal_to = "Is Less Than Or Equal To",
  is_less_than = "Is Less Than",
  is_set = "Is Set",
  is_not_set = "Is Not Set",
  is_not_equal = "Is Not Equal",
  is_true = "Is True",
  is_false = "Is False",
  contains = "Contains",
  not_contains = "Does Not Contain",
}

export const CONDITION_OPERATORS_HELP_TEXT: Partial<Record<keyof typeof CONDITION_OPERATORS, string>> = {
  is_true:
    "The value must be a true boolean value. This will check that the value provided can be evaluated to a string 'True' or 'true'",
  is_false:
    "The value must be a false boolean value. This will check that the value provided can be evaluated to a string 'False' or 'false'",
};

export enum CONDITION_LOGIC_TYPES {
  all = "All of the conditions are met",
  any = "Any of the conditions are met",
}

export interface ICondition {
  condition_form?: FormConfiguration["artifact_guid"] | FormConfiguration["id"];
  condition_field: FormField["artifact_guid"] | FormField["id"];
  condition_operator: keyof typeof CONDITION_OPERATORS;
  condition_value: string | number | boolean;
}

export const ConditionValidationSchema: schemaBuilder.SchemaOf<ICondition> = schemaBuilder.object().shape({
  condition_form: Many2OneUUIDPKBaseSchema("A form field is required when adding a condition.").optional().nullable(),
  condition_field: Many2OneUUIDPKBaseSchema("A form field is required when adding a condition.")
    .required("A form field is required when adding a condition.")
    .typeError("Value must be the artifact GUID of the form field"),
  condition_operator: schemaBuilder
    .mixed()
    .oneOf(Object.keys(CONDITION_OPERATORS))
    .required("Condition Operator is required"),
  condition_value: schemaBuilder
    .string()
    .nullable()
    .optional()
    .when(["condition_operator"], {
      is: (condition_operator: keyof typeof CONDITION_OPERATORS) =>
        !["is_not_set", "is_set"].includes(condition_operator),
      then: schemaBuilder.string().required("Condition Value is required"),
    }),
});

export const isValueBasedCondition = (condition_operator: ICondition["condition_operator"]) => {
  const nonValueOperators: ICondition["condition_operator"][] = ["is_set", "is_not_set", "is_true", "is_false"];
  return !nonValueOperators.includes(condition_operator);
};
